import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { assign } from 'lodash-es';
import SignaturePad, { Options, PointGroup } from 'signature_pad';
import { SignaturePadOutputFile, SignaturePadOutputFileType } from './signature-pad.constant';
import { getSignaturePadConfigDefault } from './signature-pad.utils';

export interface RedSignaturePadOptions extends Options {
  canvasHeight?: number;
  canvasWidth?: number;
}
@Directive()
export abstract class SignatureDrawerBase implements ControlValueAccessor, OnInit, OnDestroy {
  private signaturePad!: SignaturePad;
  _value!: any;
  private options: RedSignaturePadOptions = {};
  @Input() outputType: SignaturePadOutputFileType = 'PNG';
  @Output() public drawStart = new EventEmitter<MouseEvent | Touch>();
  @Output() public drawBeforeUpdate = new EventEmitter<MouseEvent | Touch>();
  @Output() public drawAfterUpdate = new EventEmitter<MouseEvent | Touch>();
  @Output() public drawEnd = new EventEmitter<MouseEvent | Touch>();
  /** Event emitted when the selected value has been changed by the user. */
  @Output() readonly valueChange: EventEmitter<any> = new EventEmitter<any>();
  constructor(protected elementRef: ElementRef<HTMLCanvasElement>, protected _changeDetectorRef: ChangeDetectorRef) {}
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected onTouched = () => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _onChange = (_value: any) => {};
  ngOnInit(): void {
    const canvas: HTMLCanvasElement = this.getCanvas();
    this.options = assign({}, getSignaturePadConfigDefault(this.outputType));
    if (this.options.canvasHeight) {
      canvas.height = this.options.canvasHeight;
    }

    if (this.options.canvasWidth) {
      canvas.width = this.options.canvasWidth;
    }

    this.redrawCanvas();
    this.signaturePad = new SignaturePad(canvas, this.options);
    this.signaturePad.addEventListener('beginStroke', event =>
      this.beginStroke((event as CustomEvent).detail as MouseEvent | Touch)
    );
    this.signaturePad.addEventListener('beforeUpdateStroke', event =>
      this.beforeUpdateStroke((event as CustomEvent).detail as MouseEvent | Touch)
    );
    this.signaturePad.addEventListener('afterUpdateStroke', event =>
      this.afterUpdateStroke((event as CustomEvent).detail as MouseEvent | Touch)
    );
    this.signaturePad.addEventListener('endStroke', event =>
      this.endStroke((event as CustomEvent).detail as MouseEvent | Touch)
    );
  }
  writeValue(value: any): void {
    if (typeof value === 'string' && value) {
      const dataURL = value;
      const canvas: HTMLCanvasElement = this.getCanvas();
      const ctx = canvas.getContext('2d');

      if (!ctx) return;

      // Create an image to check and control dimensions
      const img = new Image();

      img.onload = () => {
        // Attempt to load dataURL with precise dimensions
        try {
          // Calculate centering offsets
          const xOffset = img.width < canvas.width ? Math.floor((canvas.width - img.width) / 2) : 0;
          const yOffset = img.height < canvas.height ? Math.floor((canvas.height - img.height) / 2) : 0;

          this.signaturePad.fromDataURL(dataURL, {
            width: img.width,
            height: img.height,
            xOffset,
            yOffset,
          });
        } catch (error) {
          console.error('Failed to load signature from dataURL', error);
        }
      };

      img.onerror = () => {
        console.error('Failed to load signature image');
        this.signaturePad.clear();
      };

      // Start loading the image
      img.src = dataURL;
    } else {
      this.signaturePad.clear();
    }
  }

  registerOnChange(onChange: (value: any) => void) {
    this._onChange = onChange;
  }

  registerOnTouched(onTouched: () => void) {
    this.onTouched = onTouched;
  }
  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.signaturePad?.off();
    } else {
      this.signaturePad?.on();
    }
  }

  /** Emits change event to set the model value. */
  private _propagateChanges(fallbackValue?: any): void {
    const valueToEmit = this.signaturePad.toDataURL(SignaturePadOutputFile[this.outputType], 0.5);
    this._value = valueToEmit;
    this.valueChange.emit(valueToEmit);
    this._onChange(valueToEmit);
    this._changeDetectorRef.markForCheck();
  }

  ngOnDestroy(): void {
    this.signaturePad?.off();
  }

  getCanvas(): HTMLCanvasElement {
    return this.elementRef.nativeElement;
  }

  /**
   * notify subscribers on signature begin
   */
  public beginStroke(event: MouseEvent | Touch): void {
    this.drawStart.emit(event);
  }

  public beforeUpdateStroke(event: MouseEvent | Touch): void {
    this.drawBeforeUpdate.emit(event);
  }

  public afterUpdateStroke(event: MouseEvent | Touch): void {
    this.drawAfterUpdate.emit(event);
  }

  /**
   * notify subscribers on signature end
   */
  public endStroke(event: MouseEvent | Touch): void {
    this._propagateChanges();
    this.drawEnd.emit(event);
  }

  /**
   * Clears the canvas
   */
  public clear(): void {
    this.signaturePad.clear();
    this._propagateChanges();
  }

  /**
   * Returns true if canvas is empty, otherwise returns false
   */
  public isEmpty(): boolean {
    return this.signaturePad.isEmpty();
  }

  /**
   * Unbinds all event handlers
   */
  public off(): void {
    this.signaturePad.off();
  }

  /**
   * Rebinds all event handlers
   */
  public on(): void {
    this.signaturePad.on();
  }

  // Returns signature image as an array of point groups
  public toData(): Array<PointGroup> {
    if (this.signaturePad) {
      return this.signaturePad.toData();
    } else {
      return [];
    }
  }

  // Draws signature image from an array of point groups
  public fromData(points: Array<PointGroup>): void {
    this.signaturePad.fromData(points as any);
  }

  /**
   * Redraw or Resize canvas, note this will clear data.
   */
  public redrawCanvas(): void {
    const canvas: HTMLCanvasElement = this.getCanvas();
    // when zoomed out to less than 100%, for some very strange reason,
    // some browsers report devicePixelRatio as less than 1, and only part of the canvas is cleared then.
    const ratio: number = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d')?.scale(ratio, ratio);
    this.signaturePad?.clear(); // otherwise isEmpty() might return incorrect value
  }
}
